import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  useFieldArray,
  useWatch,
  type UseFormReturn,
} from 'react-hook-form';

import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Menu,
  Skeleton,
  MenuItem,
} from '@mui/material';
import {
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';

import type { TripLeg } from '../../../../data/models/reservation.model';
import type { Provider } from '../../../../data/models/provider.model';

import tripLegUtils from '../../../../services/triplegUtils';
import { phoneUtils } from '../../../../services/phoneUtils';

import * as lookupSlice from '../../../../data/lookup.slice';
import * as triplegSlice from '../../../../data/tripleg.slice';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';

import { TripSummary } from '../trip-summary/TripSummary';
import Checkbox from '../../../forms/Checkbox';

const PREFIX = 'ProviderCallForm';
const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  details: `${PREFIX}-details`,
  actions: `${PREFIX}-actions`,
  provider: `${PREFIX}-provider`,
  providerName: `${PREFIX}-providerName`,
  providerContact: `${PREFIX}-providerContact`,
  providerPhone: `${PREFIX}-providerPhone`,
  exception: `${PREFIX}-exception`,
  fieldset: `${PREFIX}-fieldset`,
  fieldLabel: `${PREFIX}-field-label`,
  fieldSelect: `${PREFIX}-fieldSelect`,
  radioGroup: `${PREFIX}-radioGroup`,
  radioSubGroup: `${PREFIX}-radioSubGroup`,
  currentTripLeg: `${PREFIX}-currentTripLeg`,
  reducedWidth: `${PREFIX}-reducedWidth`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    placeItems: 'flex-start',
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(4),
    minWidth: 600,
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    ['& .MuiButton-root']: {
      width: '144px',
    },
  },

  [`& .${classes.providerName}`]: {
    marginBottom: theme.spacing(0.5),
    paddingBottom: 0,
  },
  [`& .${classes.providerContact}`]: {
    paddingBottom: 0,
  },
  [`& .${classes.providerPhone}`]: {
    color: theme.palette.lightTextSecondary.main,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    '& .MuiSvgIcon-root:first-of-type': {
      marginLeft: 4,
    },
  },

  [`& .${classes.fieldLabel}`]: {
    fontWeight: '600',
    marginBottom: theme.spacing(1),
    paddingBottom: 0,
  },
  [`& .${classes.fieldSelect}`]: {
    marginLeft: theme.spacing(4),
  },
  [`& .${classes.radioGroup}`]: {
    display: 'flex',
    marginBottom: theme.spacing(4),
    flexDirection: 'column',

    '& .MuiFormControlLabel-root': {
      marginLeft: 0,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',

      '& .MuiTypography-root': {
        paddingBottom: 0,
      },
    },
  },
  [`& .${classes.radioSubGroup}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(5),
  },
  [`& .${classes.currentTripLeg}`]: {
    color: theme.palette.error.main,
  },
}));

const StyledMenuButton = styled(Box)(({ theme }) => ({
  width: '337px',
  height: '100%',
  padding: theme.spacing(1.5),
  marginLeft: theme.spacing(5),
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  background: '#FFFFFF',
  borderRadius: '3px',
  boxShadow: '0px 2px 7px 0px #01101226',
  color: 'theme.palette.lightTextPrimary.main',
  transition: 'width 0.2s ease-in-out',
  fontFamily: theme.typography.subtitle1.fontFamily,
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: theme.typography.subtitle1.fontWeight,
  lineHeight: theme.typography.body1.lineHeight,
  letterSpacing: theme.typography.subtitle1.letterSpacing,

  [`&.${classes.reducedWidth}`]: {
    width: '137px',
  },

  [`& .MuiSvgIcon-root`]: {
    marginLeft: 'auto',
  },
}));

const StyledDropdownGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '10px',
});

const StyledMenuList = styled(Menu)({
  ['& .MuiPaper-root']: {
    minWidth: '220px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    boxShadow: '0px 2px 7px 0px #01101226',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  ['& .MuiList-root']: {
    width: '100%',
  },
});

const StyledInputContainer = styled('div')({
  flexGrow: 1,
  height: '48px',
  padding: '12px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  background: '#FFFFFF',
  borderRadius: '3px',
  boxShadow: '0px 2px 7px 0px #01101226',
  color: '#0000000F',
});

const StyledInput = styled('input')({
  width: '100%',
  border: 'none',
  outline: 'none',
  color: '#01101299',
  fontFamily: 'Source Sans Pro',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textAlign: 'left',
  textUnderlinePosition: 'from-font',
  textDecorationSkipInk: 'none',
});

interface ReasonCheckboxesProps {
  formHook: UseFormReturn<any, any>;
  reasons: any[];
  classNames: string[];
}

const ReasonCheckboxes = ({
  formHook,
  reasons,
  classNames,
}: ReasonCheckboxesProps) => {
  return (
    <>
      {reasons.map((reason, idx) => {
        return (
          <Box
            className={classNames.join(' ')}
            key={`reason-checkboxes-${reason.callOutcomeBC}`}>
            <Checkbox
              name={`reasonOptions.${reason.callOutcomeBC}`}
              control={formHook.control}
              label={reason.callOutcomeName}
            />
          </Box>
        );
      })}
    </>
  );
};

interface ReasonDropdownProps {
  open: boolean;
  onClose: (evt: React.MouseEvent<HTMLElement>) => void;
  anchorEl: HTMLElement | null;
  selectedReasonID: number;
  handleToggle: (evt: React.MouseEvent<HTMLElement>) => void;
  handleSelect: (evt: any, id: number, reasonBC: string) => void;
  handleChange: (evt: any) => void;
  reasons: any[];
}

const ReasonDropdown = ({
  open,
  onClose,
  anchorEl,
  selectedReasonID,
  handleToggle,
  handleSelect,
  handleChange,
  reasons,
}: ReasonDropdownProps) => {
  const { t } = useTranslation('common');
  const selReason = reasons[selectedReasonID];
  const isOtherSelected = selReason?.callOutcomeBC === 'OTHER';

  return (
    <StyledDropdownGroup>
      <StyledMenuButton
        className={isOtherSelected ? classes.reducedWidth : ''}
        onClick={handleToggle}>
        {Boolean(selectedReasonID) &&
          reasons[selectedReasonID]?.callOutcomeName}
        {!selectedReasonID && t('exceptionResolve.reason')}
        {open && <ExpandLessIcon sx={{ color: '#0110128A' }} />}
        {!open && <ExpandMoreIcon sx={{ color: '#0110128A' }} />}
      </StyledMenuButton>
      <StyledMenuList open={open} onClose={onClose} anchorEl={anchorEl}>
        {reasons.map((reason, idx) => {
          return (
            <MenuItem
              key={`${reason.callOutcomeBC}`}
              onClick={(evt: React.MouseEvent<HTMLLIElement>) =>
                handleSelect(evt, idx, reason.callOutcomeBC)
              }>
              {reason.callOutcomeName}
            </MenuItem>
          );
        })}
      </StyledMenuList>
      {isOtherSelected && (
        <StyledInputContainer>
          <StyledInput
            placeholder={t('exceptionResolve.reasonOther')}
            type="text"
            maxLength={50}
            onChange={handleChange}
          />
        </StyledInputContainer>
      )}
    </StyledDropdownGroup>
  );
};

interface ExceptionResolveFormProps {
  formHook: UseFormReturn<any, any>;
  isSaving: boolean;
  isLoading: boolean;
  tripLeg: TripLeg;
  tripLegsInReservation: TripLeg[];
  provider: Provider | null;
  submit: () => void;
  close: () => void;
  userRoleBC: string;
  callOutcomes: any[];
}

function ExceptionResolveForm({
  formHook,
  isSaving,
  isLoading,
  tripLeg,
  tripLegsInReservation,
  provider,
  submit,
  close,
  userRoleBC,
  callOutcomes,
}: ExceptionResolveFormProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const isFormValid = formHook.formState.isValid;
  const isFormDirty = formHook.formState.isDirty;

  const isSubmitDisabled =
    isLoading || isSaving || !isFormValid || !isFormDirty;

  const providerPhoneUses = useAppSelector(
    lookupSlice.selectTransportationProviderPhoneUses,
  );

  // Reason selection actions
  const [reasonSelectMenuAnchorElm, setReasonSelectMenuAnchorElm] =
    useState<HTMLElement | null>(null);
  const reasonMenuOpen = Boolean(reasonSelectMenuAnchorElm);
  const [reasonID, setReasonID] = useState(0);
  const handleReasonToggle = (evt: React.MouseEvent<HTMLElement>) => {
    setReasonSelectMenuAnchorElm(
      reasonSelectMenuAnchorElm ? null : evt.currentTarget,
    );
  };
  const handleReasonSelect = (evt: any, id: number, reasonBC: string) => {
    setReasonID(id);
    setReasonSelectMenuAnchorElm(null);
  };
  const handleReasonOtherChange = (evt: any) => {
    formHook.setValue('reasonOther', evt.target.value);
  };

  // Check for dispatch in phones, fallback to first phone number
  const providerName = provider?.transportationProviderName;
  const providerDispatch = phoneUtils.getProviderDispatchPhone(
    provider?.phoneNumbers,
    providerPhoneUses,
  );
  const providerContactName = providerDispatch
    ? `${providerDispatch?.contactFirstName} ${providerDispatch?.contactLastName}`
    : '';
  const providerDispatchPhone = providerDispatch?.phoneNumber;
  const providerContactPhone = providerDispatchPhone
    ? phoneUtils.formatPhoneString(providerDispatchPhone, false, false)
    : '';

  const handleClose = () => {
    close();
  };

  useEffect(() => {
    dispatch(
      triplegSlice.fetchAllTripLegsInReservationAsync(
        tripLeg.reservationReferenceID!,
        tripLeg.passengerUUID!,
      ),
    );
  }, []);

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.form}>
        <Box className={classes.provider}>
          {isLoading && (
            <>
              <Skeleton variant="text" width={300} height={36} />
              <Skeleton variant="text" width={150} height={24} />
              <Skeleton variant="text" width={200} height={24} />
            </>
          )}
          {!isLoading && (
            <>
              <Typography variant="h3" className={classes.providerName}>
                {providerName || ''}
              </Typography>
              {providerContactName && (
                <>
                  <Typography
                    variant="body1"
                    className={classes.providerContact}>
                    {providerContactName ?? ''}
                  </Typography>
                  <Typography variant="body1" className={classes.providerPhone}>
                    {providerContactPhone ?? ''}
                    <ChatBubbleOutlineIcon fontSize="small" />
                    <PhoneIcon fontSize="small" />
                  </Typography>
                </>
              )}
            </>
          )}
        </Box>

        <Box className={classes.fieldset}>
          <Typography className={classes.fieldLabel} variant="body1">
            {t('exceptionResolve.exception')}
          </Typography>
          <Typography variant="body1" className={classes.exception}>
            {tripLeg.tripLegExceptionBC
              ? t(
                  tripLegUtils.getExceptionTranslationString(
                    tripLeg.tripLegExceptionBC,
                  ) ?? tripLeg.tripLegExceptionBC,
                )
              : ''}
          </Typography>
        </Box>

        <Box className={classes.fieldset}>
          <Typography className={classes.fieldLabel} variant="body1">
            {t('exceptionResolve.whatWasTheOutcome')}
          </Typography>
          <FormControl>
            <Controller
              name="resolveReason"
              control={formHook.control}
              render={({ field }) => (
                <RadioGroup className={classes.radioGroup} {...field}>
                  {callOutcomes
                    .filter((callOutcome) =>
                      callOutcome.role.includes(userRoleBC),
                    )
                    .map((callOutcome) => {
                      return (
                        <Fragment key={callOutcome.callOutcomeBC}>
                          <FormControlLabel
                            value={callOutcome.callOutcomeBC}
                            control={<Radio />}
                            label={callOutcome.callOutcomeName}
                          />
                          {callOutcome.childrenSelection === 'multiple' &&
                            callOutcome.children &&
                            callOutcome.children.length > 0 && (
                              <ReasonCheckboxes
                                formHook={formHook}
                                reasons={callOutcome.children}
                                classNames={[classes.radioSubGroup]}
                              />
                            )}
                          {callOutcome.childrenSelection === 'single' &&
                            callOutcome.children &&
                            callOutcome.children.length > 0 && (
                              <ReasonDropdown
                                open={reasonMenuOpen}
                                onClose={handleReasonToggle}
                                anchorEl={reasonSelectMenuAnchorElm}
                                selectedReasonID={reasonID}
                                handleToggle={handleReasonToggle}
                                handleSelect={handleReasonSelect}
                                handleChange={handleReasonOtherChange}
                                reasons={callOutcome.children}
                              />
                            )}
                        </Fragment>
                      );
                    })}
                </RadioGroup>
              )}
            />
          </FormControl>
        </Box>

        <Box className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            disabled={isSubmitDisabled}
            size="large"
            onClick={submit}>
            {t('common.next')}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleClose}
            size="large">
            {t('common.close')}
          </Button>
        </Box>
      </Paper>
      <TripSummary
        tripLeg={tripLeg}
        tripLegsInReservation={tripLegsInReservation}
        providerContactName={providerContactName ?? ''}
        isLoading={isLoading}
      />
    </StyledBox>
  );
}

export { ExceptionResolveForm };
